/**
 * الشريط العلوي — 64px: العنوان والسطر التحتاني · بحث · جرس · هوية المستخدم.
 * القيم من design/Main.dc.html.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { fmtNum } from "@/lib/format";

export type TopBarProps = {
  title: string;
  /** السطر التحتاني — «7 مهام · 2 متأخرة · الثلاثاء 18 أغسطس» */
  crumb?: ReactNode;
  search?: {
    value: string;
    onChange: (v: string) => void;
    onSubmit?: (v: string) => void;
    placeholder?: string;
  };
  unreadCount?: number;
  onBellClick?: () => void;
  user?: { name: string; roleTitle?: string | null };
  onSignOut?: () => void;
};

/** أول حرف من الاسم للأڤاتار — «أ. مشرفة الدعم أ» ⇒ «هـ» زي التصميم */
function avatarLetter(name: string): string {
  const cleaned = name.replace(/^(أ\.|د\.|م\.)\s*/u, "").trim();
  const first = cleaned.charAt(0) || "؟";
  // الهاء المفردة بتتعرض «هـ» عشان ما تتلخبطش مع التاء المربوطة
  return first === "ه" ? "هـ" : first;
}

export function TopBar({
  title,
  crumb,
  search,
  unreadCount = 0,
  onBellClick,
  user,
  onSignOut,
}: TopBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // اختصار «/» بيوقّف المؤشر في البحث — الفريق بيدوّر باسم الأسرة طول اليوم
  useEffect(() => {
    if (!search) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [search]);

  return (
    <header className="top">
      <div className="top-heading">
        <div className="top-title clip">{title}</div>
        {crumb ? <div className="top-crumb clip">{crumb}</div> : null}
      </div>

      {search ? (
        <form
          className="top-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            search.onSubmit?.(search.value);
          }}
        >
          <Icon name="search" size={15} stroke="#94A3B8" />
          <input
            ref={inputRef}
            type="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={search.placeholder ?? "ابحث باسم الأسرة أو الرقم…"}
            aria-label="بحث"
          />
          {!focused && !search.value ? <span className="kbd">/</span> : null}
        </form>
      ) : (
        <span style={{ marginInlineStart: "auto" }} />
      )}

      <button
        type="button"
        className="bell"
        onClick={onBellClick}
        aria-label={unreadCount > 0 ? `الإشعارات — ${fmtNum(unreadCount)} جديدة` : "الإشعارات"}
        title={unreadCount > 0 ? `${fmtNum(unreadCount)} إشعارات جديدة` : "الإشعارات"}
      >
        <Icon name="bell" size={17} stroke="#475569" />
        {unreadCount > 0 ? <span className="bell-dot" /> : null}
      </button>

      {user ? (
        <div className="me">
          <div className="avatar" aria-hidden="true">
            {avatarLetter(user.name)}
          </div>
          <div>
            <div className="me-name">{user.name}</div>
            {user.roleTitle ? <div className="me-role">{user.roleTitle}</div> : null}
          </div>
          {onSignOut ? (
            <button
              type="button"
              className="icon-btn"
              onClick={onSignOut}
              aria-label="تسجيل الخروج"
              title="تسجيل الخروج"
            >
              <Icon name="logout" size={17} />
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
